import { NgClass, NgForOf, NgIf } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CentsCurrencyPipe } from '@common/centsCurrency.pipe';
import { CustomerTestsTypes } from '@enums/customer-tests-types';
import { environment } from '@environments/environment';
import { ResultService } from '@Medology/ng-findalab';
import { OrderUpgradeSchema } from '@models/order-upgrade-schema';
import { PaginatedResponse } from '@models/paginatedResponse';
import { Test } from '@models/test';
import { Upgrade } from '@models/upgrade';
import { ErrorLoggingService } from '@services/error-logging.service';
import { OrderService } from '@services/order.service';
import { StorageService } from '@services/storage.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-order-upgrade-card',
  imports: [CentsCurrencyPipe, NgForOf, NgIf, NgClass],
  templateUrl: './order-upgrade-card.component.html',
  styleUrl: './order-upgrade-card.component.scss',
})
export class OrderUpgradeCardComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Output() public upgradeAvailabilityChanged = new EventEmitter<boolean>();

  @ViewChild('testsGrid') private testsGrid: ElementRef<HTMLElement>;

  public upgrade: Upgrade;
  public singleTestUpgrade: string;
  public extraCost: number;
  public customTitle: string;
  public isExpanded = false;
  public showReadMore = false;
  public upgradeItems: string[] = [];
  public shouldDisplayAdditionalTests: boolean;

  private readonly collapsedMaxHeight = 195;

  private changeDetectorRef = inject(ChangeDetectorRef);
  private orderService = inject(OrderService);
  private storageService = inject(StorageService);
  private errorLoggingService = inject(ErrorLoggingService);
  private resultService = inject(ResultService);
  private labSubscription: Subscription;
  private testSubscription: Subscription;
  private cartTests: Test[] = [];
  private upgradeSchema: OrderUpgradeSchema[];

  /**
   * Initializes the component.
   */
  public ngOnInit() {
    this.getUpgradeSchema();
    this.listenForTestChanges();
    this.listenForLabChanges();
  }

  /**
   * Runs after the view has been checked.
   */
  public ngAfterViewChecked(): void {
    this.handleReadMore();
  }

  /**
   * Toggles the expanded state of the tests container.
   */
  public toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  /**
   * Cleans up subscriptions.
   */
  public ngOnDestroy() {
    this.testSubscription?.unsubscribe();
    this.labSubscription?.unsubscribe();
  }

  /**
   * Upgrades the current order with the selected upgrade.
   */
  public upgradeOrder() {
    const test: Test = {
      id: this.upgrade.id,
      name: this.upgrade.name,
      price: this.upgrade.price,
      slug: this.upgrade.slug,
    };
    this.clearUpgradeState();

    this.orderService.loadCart([test]).subscribe({
      error: (error) => {
        this.errorLoggingService.logHttpError(error, 'OrderUpgradeCardComponent - upgradeOrder');
        this.getUpgrades(this.storageService.center?.id);
      },
    });
  }

  /**
   * Gets available upgrades for the current order.
   *
   * @param {number} labId - Optional lab identifier to filter upgrades that are available at a specific lab.
   */
  private getUpgrades(labId?: number): void {
    this.cartTests = this.storageService.tests;
    const testIds = this.cartTests.map((test) => test.id);

    this.orderService.getAvailableUpgrades(testIds, environment.siteId, labId).subscribe({
      next: (response) => this.handleUpgradeResponse(response),
      error: (error) => this.errorLoggingService.logHttpError(error, 'OrderUpgradeCardComponent - getUpgrades'),
    });
  }

  /**
   * Handles the response from the order upgrade request.
   *
   * @param {PaginatedResponse<Upgrade>} upgradeData - The paginated response data containing available upgrades.
   */
  private handleUpgradeResponse(upgradeData: PaginatedResponse<Upgrade>): void {
    if (!upgradeData.data.length) {
      this.upgradeAvailabilityChanged.emit(false);

      return;
    }

    this.shouldDisplayAdditionalTests = this.cartTests.some((test) => test.type === CustomerTestsTypes.Panel);
    this.upgrade = upgradeData.data[0];
    this.upgradeItems = this.shouldDisplayAdditionalTests ? this.upgrade.additional_tests : this.upgrade.tests;
    this.extraCost = this.getUpgradeExtraCost();
    this.handleSingleTestUpgrade();
    this.setCustomTitle();
    this.upgradeAvailabilityChanged.emit(true);
  }

  /**
   * Get the extra cost for the upgrade.
   */
  private getUpgradeExtraCost(): number {
    const currentCartTotal = this.cartTests.reduce((total, test) => total + test.price, 0);

    return this.upgrade.price - currentCartTotal;
  }

  /**
   * Handles the scenario where only one test upgrade is available .
   */
  private handleSingleTestUpgrade() {
    const cartTestNames = this.cartTests.map((test) => test.name);
    const filteredAvailableUpgrades = this.upgrade.tests.filter((test) => !cartTestNames.includes(test));

    if (filteredAvailableUpgrades.length > 1) {
      return;
    }

    this.singleTestUpgrade = filteredAvailableUpgrades[0];
  }

  /**
   * Clears the current upgrade state.
   */
  private clearUpgradeState(): void {
    this.upgrade = null;
    this.singleTestUpgrade = null;
    this.extraCost = null;
    this.customTitle = null;
    this.isExpanded = false;
    this.showReadMore = false;
  }

  /**
   * Listens for changes in the selected tests and updates upgrades accordingly.
   */
  private listenForTestChanges(): void {
    this.testSubscription = this.storageService.testsSubject.subscribe({
      next: () => {
        this.clearUpgradeState();
        this.getUpgrades(this.storageService.center?.id);
      },
    });
  }

  /**
   * Listens for changes in the selected lab and updates upgrades accordingly.
   */
  private listenForLabChanges(): void {
    this.labSubscription = this.resultService.resultSelected$.subscribe({
      next: (lab) => {
        this.clearUpgradeState();
        this.getUpgrades(lab.id);
      },
    });
  }

  /**
   * Fetches the upgrade schema.
   */
  private getUpgradeSchema(): void {
    this.orderService.getUpgradeSchema().then(
      (schema) => (this.upgradeSchema = schema),
      (error) => this.errorLoggingService.logHttpError(error, 'OrderUpgradeCardComponent - getUpgradeSchema'),
    );
  }

  /**
   * Sets the customTitle for the upgrade card based on the upgrade schema.
   */
  private setCustomTitle(): void {
    this.customTitle = this.upgradeSchema[this.upgrade.slug]?.customTitle ?? null;
  }

  /**
   * Checks if the "Read More" button should be displayed based on the content height of the tests grid.
   */
  private handleReadMore(): void {
    if (!this.testsGrid?.nativeElement || this.showReadMore) {
      return;
    }

    const contentHeight = this.testsGrid.nativeElement.scrollHeight;
    this.showReadMore = contentHeight > this.collapsedMaxHeight;
    this.changeDetectorRef.detectChanges();
  }
}
